import { useLanguage } from '../i18n/LanguageContext';
import { useCallback, useState, useEffect } from 'react';
import { claimCredit, fetchCredits, fetchCreditDetail } from '../api/credits';
import Pagination from '../components/Pagination';
import CreditDeductionModal from '../components/CreditDeductionModal';
import { can } from '../utils/permissions';
import './CreditsPage.css';

// Reason 라벨 매핑
const reasonLabel = {
  Coupon: '쿠폰',
  Event: '이벤트',
  Refund: '환불',
  PlayGame: '게임 이용',
  Purchase: '구매',
  Claim: '지급',
};

const getReasonBadgeClass = (reason, amount = 0) => {
  if (reason === 'Claim' && amount < 0) return 'spend';
  if (reason === 'Coupon') return 'earn';
  if (reason === 'Event' || reason === 'Claim') return 'claim';
  if (reason === 'Refund') return 'refund';
  if (amount < 0) return 'spend';
  return 'earn';
};

const TX_PAGE_SIZE = 30;
const LIST_PAGE_SIZE = 50;

function CreditsPage() {
  const { t } = useLanguage();
  const canClaimCredit = can('credit.claim');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [deductionUser, setDeductionUser] = useState(null);
  const [claimUserUuid, setClaimUserUuid] = useState('');
  const [claimAmount, setClaimAmount] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState('');
  const [txPage, setTxPage] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const offset = (currentPage - 1) * LIST_PAGE_SIZE;
      const res = await fetchCredits({
        offset,
        limit: LIST_PAGE_SIZE,
        search: searchTerm.trim(),
      });
      setUsers(res.data.users ?? []);
      setTotalUsers(Number(res.data.total ?? 0));
    } catch (err) {
      setError(err.response?.data?.message || '크레딧 목록을 불러오지 못했습니다.');
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredUsers = users;

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
    setCurrentPage(1);
  };

  const formatDate = (dateString, withTime = false) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const datePart = `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}`;
    if (withTime) {
      return `${datePart} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    }
    return datePart;
  };

  // Drawer 열 때 트랜잭션 페이지 초기화
  const openDrawer = async (user) => {
    const walletUuid = user.wallets[0]?.uuid;

    setSelectedUser({ ...user, transactions: [] });
    setTxPage(1);
    setDetailError('');

    if (!walletUuid) {
      setDetailError('Wallet UUID가 없어 상세 정보를 불러올 수 없습니다.');
      return;
    }

    try {
      setDetailLoading(true);
      const detail = await fetchCreditDetail(walletUuid, {
        username: user.username,
        email: user.email,
      });
      setSelectedUser(detail);
    } catch (err) {
      setDetailError(err.response?.data?.message || '크레딧 상세 정보를 불러오지 못했습니다.');
    } finally {
      setDetailLoading(false);
    }
  };

  // 트랜잭션 페이징
  const getTxPageData = (transactions) => {
    const start = (txPage - 1) * TX_PAGE_SIZE;
    return transactions.slice(start, start + TX_PAGE_SIZE);
  };

  const getTxTotalPages = (transactions) => {
    return Math.ceil(transactions.length / TX_PAGE_SIZE);
  };

  const closeDrawer = () => {
    setSelectedUser(null);
  };

  const getClaimErrorMessage = (err) => (
    err?.response?.data?.message || err?.message || '크레딧 지급에 실패했습니다.'
  );

  const getPaginationInfo = () => {
    if (searchTerm) {
      return `현재 페이지 검색 결과 ${filteredUsers.length.toLocaleString()}명 / 전체 ${totalUsers.toLocaleString()}명`;
    }

    if (totalUsers === 0) return '총 0명 표시';

    const start = (currentPage - 1) * LIST_PAGE_SIZE + 1;
    const end = Math.min(start + users.length - 1, totalUsers);
    return `총 ${totalUsers.toLocaleString()}명 중 ${start.toLocaleString()}-${end.toLocaleString()} 표시`;
  };

  const closeClaimModal = () => {
    if (claimLoading) return;
    setClaimModalOpen(false);
    setClaimError('');
  };

  const openClaimModal = ({ userUuid = '' } = {}) => {
    if (!canClaimCredit) return;
    setClaimUserUuid(userUuid);
    setClaimAmount('');
    setClaimError('');
    setClaimModalOpen(true);
  };

  const handleClaimCredit = async () => {
    if (!canClaimCredit) return;
    if (!claimUserUuid || !claimAmount) return;

    try {
      setClaimLoading(true);
      setClaimError('');
      const result = await claimCredit({
        userUuid: claimUserUuid.trim(),
        amount: Number(claimAmount),
      });
      await loadData();

      const walletUuid = result.wallet?.uuid;

      if (walletUuid && selectedUser?.wallets[0]?.uuid === walletUuid) {
        const detail = await fetchCreditDetail(walletUuid, {
          username: selectedUser.username,
          email: selectedUser.email,
        });
        setSelectedUser(detail);
        setTxPage(1);
      }

      setClaimModalOpen(false);
      setClaimUserUuid('');
      setClaimAmount('');
    } catch (err) {
      console.error('Failed to claim credit:', err);
      setClaimError(getClaimErrorMessage(err));
    } finally {
      setClaimLoading(false);
    }
  };

  const handleDeductionSuccess = async (result) => {
    const wallet = result?.wallet;
    if (!wallet?.uuid || wallet.uuid !== deductionUser?.wallets?.[0]?.uuid) {
      throw new Error('Unexpected deduction response');
    }
    // Retain the confirmed server balance even if a subsequent read fails.
    setSelectedUser(prev => prev?.wallets?.[0]?.uuid === wallet.uuid
      ? { ...prev, wallets: [wallet] } : prev);
    await loadData();
    try {
      setDetailLoading(true);
      setDetailError('');
      const detail = await fetchCreditDetail(wallet.uuid, {
        username: deductionUser.username,
        email: deductionUser.email,
      });
      setSelectedUser(prev => prev?.wallets?.[0]?.uuid === wallet.uuid ? detail : prev);
      setTxPage(1);
    } catch {
      setDetailError(t('차감은 완료되었지만 거래내역을 불러오지 못했습니다. 다시 차감하지 말고 상세를 다시 열어주세요.'));
    } finally {
      setDetailLoading(false);
    }
    setDeductionUser(null);
  };

  const renderTransactionContent = () => {
    const transactions = selectedUser?.transactions || [];

    if (detailLoading) {
      return <p className="empty-text">{t("Transaction 내역을 불러오는 중입니다...")}</p>;
    }

    if (detailError) {
      return <p className="empty-text" style={{ color: '#dc2626' }}>{detailError}</p>;
    }

    if (transactions.length === 0) {
      return <p className="empty-text">{t("Transaction 내역이 없습니다.")}</p>;
    }

    return (
      <>
        <div className="mini-table-wrap tx-modal-table-wrap">
          <table className="mini-table">
            <thead>
              <tr>
                <th>Reason</th>
                <th>Amount</th>
                <th>Reference</th>
                <th>Description</th>
                <th>{t("일시")}</th>
              </tr>
            </thead>
            <tbody>
              {getTxPageData(transactions).map(tx => (
                <tr key={tx.uuid}>
                  <td>
                    <span className={`reason-badge ${getReasonBadgeClass(tx.reason, tx.amount)}`}>
                      {tx.reason === 'Claim' ? t(tx.amount < 0 ? '회수' : '지급') : reasonLabel[tx.reason] ?? tx.reason}
                    </span>
                  </td>
                  <td className={tx.amount > 0 ? 'amount-plus' : 'amount-minus'}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()}
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>
                    {tx.reference || '-'}
                  </td>
                  <td style={{ fontSize: '12px' }}>{tx.description}</td>
                  <td style={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(tx.createdAt, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {getTxTotalPages(transactions) > 1 && (
          <div className="tx-pagination">
            <button
              className={`tx-page-btn ${txPage <= 1 ? 'disabled' : ''}`}
              onClick={() => txPage > 1 && setTxPage(txPage - 1)}
            >{t("이전")}</button>
            {Array.from({ length: getTxTotalPages(transactions) }, (_, i) => (
              <button
                key={i + 1}
                className={`tx-page-btn ${txPage === i + 1 ? 'active' : ''}`}
                onClick={() => setTxPage(i + 1)}
              >{i + 1}</button>
            ))}
            <button
              className={`tx-page-btn ${txPage >= getTxTotalPages(transactions) ? 'disabled' : ''}`}
              onClick={() => txPage < getTxTotalPages(transactions) && setTxPage(txPage + 1)}
            >{t("다음")}</button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="credits-page">
      {/* 헤더 */}
      <div className="page-header">
        <div>
          <h2 className="page-title">{t("크레딧 관리")}</h2>
          <p className="page-subtitle">{t("사용자 크레딧을 조회하고, 사용 기록 확인 및 크레딧 수정을 할 수 있습니다.")}</p>
        </div>
        <div className="header-actions">
          <form className="search-form" onSubmit={handleSearchSubmit}>
            <div className="search-box">
              <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder={t("username, 이메일, Wallet UUID, holderUuid 입력 후 Enter")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <button className="btn-search" type="submit" disabled={loading}>
              {t("검색")}
            </button>
          </form>
          <button
            className="btn-claim"
            disabled={!canClaimCredit}
            title={!canClaimCredit ? '크레딧 지급 권한이 없습니다.' : undefined}
            onClick={() => openClaimModal()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            {t("크레딧 수동 지급")}
          </button>
        </div>
      </div>

      {/* 테이블 */}
      <div className="table-card">
        <table className="credits-table">
          <thead>
            <tr>
              <th>username</th>
              <th>email</th>
              <th>Wallet UUID</th>
              <th style={{ textAlign: 'center' }}>Wallet Type</th>
              <th style={{ textAlign: 'right', paddingRight: '20px' }}>balance</th>
              <th style={{ textAlign: 'center' }}>{t("관리")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>{t("데이터를 불러오는 중입니다...")}</td></tr>
            ) : error ? (
              <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#dc2626' }}>{error}</td></tr>
            ) : filteredUsers.length > 0 ? (
              filteredUsers.map(user => (
                <tr
                  key={user.wallets[0]?.uuid || user.userUuid}
                  className="credit-row"
                  onClick={() => openDrawer(user)}
                >
                  <td className="detail-text" style={{ fontWeight: 600 }}>{user.username}</td>
                  <td className="detail-text">{user.email}</td>
                  <td className="detail-text" style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6b7280' }}>{user.wallets[0]?.uuid || '-'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`status-badge ${user.wallets[0]?.type?.toLowerCase() || ''}`}>
                      {user.wallets[0]?.type || '-'}
                    </span>
                  </td>
                  <td className="balance-text" style={{ textAlign: 'right', paddingRight: '20px' }}>{user.wallets[0]?.balance?.toLocaleString() ?? '-'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button className="action-btn" onClick={(e) => {
                      e.stopPropagation();
                      openDrawer(user);
                    }}>
                      {t("상세 보기")}
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
                  "{searchTerm}" 에 대한 검색 결과가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <Pagination
          currentPage={currentPage}
          total={totalUsers}
          pageSize={LIST_PAGE_SIZE}
          info={getPaginationInfo()}
          disabled={loading}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Drawer Overlay */}
      {selectedUser && (
        <div className="drawer-overlay" onClick={closeDrawer}></div>
      )}

      {/* Drawer Panel */}
      <div className={`detail-drawer credit-detail-drawer ${selectedUser ? 'open' : ''}`}>
        {selectedUser && (
          <div className="credit-detail-layout">
            <div className="credit-transaction-side">
              <section className="credit-transaction-panel">
                <div className="credit-transaction-header">
                  <div>
                    <span className="panel-eyebrow">Transaction</span>
                    <h3>{t("거래내역")}</h3>
                  </div>
                  <span className="credit-tx-count">{selectedUser.transactions?.length || 0}건</span>
                </div>
                <div className="tx-inline-summary">
                  <div className="tx-summary-item">
                    <span>{t("사용자")}</span>
                    <strong>{selectedUser.username || '-'}</strong>
                  </div>
                  <div className="tx-summary-item">
                    <span>{t("현재 잔액")}</span>
                    <strong>{selectedUser.wallets[0]?.balance?.toLocaleString() ?? '-'} credit</strong>
                  </div>
                  <div className="tx-summary-item">
                    <span>{t("거래 건수")}</span>
                    <strong>{selectedUser.transactions?.length || 0}건</strong>
                  </div>
                </div>
                <div className="credit-transaction-content">
                  {renderTransactionContent()}
                </div>
              </section>
            </div>

            <div className="credit-detail-main">
              <div className="drawer-header">
                <h3>{t("크레딧 상세 정보")}</h3>
                <button className="drawer-close-btn" onClick={closeDrawer}>
                  <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>

              <div className="drawer-content">
                {/* 사용자 요약 */}
                <div className="drawer-profile-card">
                  <div className="drawer-profile-info">
                    <div className="drawer-name">
                      {selectedUser.username}
                    </div>
                    <div className="drawer-sub">{selectedUser.email}</div>
                  </div>
                </div>

                {/* 섹션 1: 사용자 기본 정보 */}
                <div className="drawer-section">
                  <h4>{t("사용자 기본 정보")}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>username</label>
                      <span>{selectedUser.username}</span>
                    </div>
                    <div className="info-item">
                      <label>email</label>
                      <span>{selectedUser.email}</span>
                    </div>
                    <div className="info-item">
                      <label>User UUID</label>
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>{selectedUser.userUuid}</span>
                    </div>
                  </div>
                </div>

                {/* 섹션 2: Wallet 정보 */}
                <div className="drawer-section">
                  <h4>{t("Wallet 정보")}</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Wallet UUID</label>
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>{selectedUser.wallets[0]?.uuid || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>Type</label>
                      <span>{selectedUser.wallets[0]?.type || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("balance (현재 잔액)")}</label>
                      <span style={{ fontWeight: 700, fontSize: '16px' }}>{selectedUser.wallets[0]?.balance?.toLocaleString() ?? '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("거래 건수")}</label>
                      <span>{selectedUser.transactions?.length ?? 0}</span>
                    </div>
                    <div className="info-item">
                      <label>holderUuid</label>
                      <span style={{ fontSize: '11px', color: '#6b7280' }}>{selectedUser.wallets[0]?.holderUuid || '-'}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("생성일 (CreatedAt)")}</label>
                      <span>{formatDate(selectedUser.wallets[0]?.createdAt, true)}</span>
                    </div>
                    <div className="info-item">
                      <label>{t("수정일 (UpdatedAt)")}</label>
                      <span>{formatDate(selectedUser.wallets[0]?.updatedAt, true)}</span>
                    </div>
                  </div>
                </div>

                {/* 섹션 3: 관리자 기능 */}
                <div className="drawer-section">
                  <h4>{t("크레딧 수정 (관리자 기능)")}</h4>
                  <div className="admin-action-card">
                    <div className="action-text">
                      <strong>{t("크레딧 수동 지급")}</strong>
                      <p>{canClaimCredit ? '이 사용자에게 크레딧을 직접 지급합니다.' : '크레딧 지급 권한이 없습니다.'}</p>
                    </div>
                    <button className="btn-secondary" disabled={!canClaimCredit} onClick={() => {
                      openClaimModal({ userUuid: selectedUser.userUuid });
                    }}>{t("지급")}</button>
                  </div>
                  <div className="admin-action-card credit-deduction-card">
                    <div className="action-text">
                      <strong>{t('크레딧 차감')}</strong>
                      <p>{t('차감할 수량과 사유를 입력합니다.')}</p>
                    </div>
                    <button
                      type="button"
                      className="credit-deduction-btn"
                      disabled={!canClaimCredit}
                      onClick={() => {
                        if (canClaimCredit) setDeductionUser(selectedUser);
                      }}
                    >{t('차감')}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {deductionUser && (
        <CreditDeductionModal user={deductionUser} allowed={canClaimCredit} onSuccess={handleDeductionSuccess} onClose={() => setDeductionUser(null)} />
      )}

      {/* 크레딧 수동 지급 모달 */}
      {claimModalOpen && (
        <>
          <div className="modal-overlay" onClick={closeClaimModal}></div>
          <div className="modal">
            <div className="modal-header">
              <h3>{t("크레딧 수동 지급")}</h3>
              <button className="drawer-close-btn" onClick={closeClaimModal}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>{t("사용자 UUID")}</label>
                <input
                  type="text"
                  placeholder="62a7aa2e-a54e-43b4-..."
                  value={claimUserUuid}
                  onChange={(e) => setClaimUserUuid(e.target.value)}
                  disabled={claimLoading}
                />
              </div>
              <div className="modal-field">
                <label>{t("크레딧 수량")}</label>
                <input
                  type="number"
                  placeholder="5000"
                  value={claimAmount}
                  onChange={(e) => setClaimAmount(e.target.value)}
                  disabled={claimLoading}
                />
              </div>
              <p className="modal-warning">
                {t("해당 사용자의 Wallet에 크레딧이 지급됩니다. 사용자 UUID와 수량을 정확히 확인하세요.")}
              </p>
              {claimError && <p className="modal-error">{claimError}</p>}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" disabled={claimLoading} onClick={closeClaimModal}>{t("취소")}</button>
              <button
                className="btn-primary"
                disabled={claimLoading || !claimUserUuid || !claimAmount || !canClaimCredit}
                onClick={handleClaimCredit}
              >
                {claimLoading ? t("지급 중...") : t("지급하기")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CreditsPage;
